import React, { useEffect, useState } from 'react';

import { AddRounded } from '@mui/icons-material';
import {
  Alert,
  Backdrop,
  CircularProgress,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Switch,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import _ from 'lodash';

import ContentCard from '../../../components/ContentCard';
import DetailsModal, {
  Button,
  Fields,
  Values,
} from '../../../components/DetailsModal';
import EventCell from '../../../components/EventCell';
import Modal from '../../../components/Modal';
import { NoPermissionMessage } from '../../../components/PageLayout';
import SkeletonStack from '../../../components/SkeletonStack';
import Tabs from '../../../components/Tabs';
import WeekCalendar from '../../../components/WeekCalendar';
import { CalendarEntry } from '../../../components/WeekCalendar/types';
import { getCurrentUserId } from '../../../services/auth';
import { useGetCoursesQuery } from '../../../services/courses';
import { Course } from '../../../services/courses/types';
import {
  useGetStaffQuery, useGetStaffsQuery,
} from '../../../services/staff';
import { ShortStaff } from '../../../services/staff/types';
import {
  useDeleteTutorialMutation,
  useGetTutorialsInRangeQuery,
  useNewTutorialMutation,
  useUpdateTutorialMutation,
} from '../../../services/tutorials';
import { Tutorial, TutorialScope } from '../../../services/tutorials/types';
import {
  getBaseAttributes, getCoursesFromPermissions,
} from '../../../services/tutorials/utils';
import { UserGroup } from '../../../types/common';
import addDays from '../../../utils/common/addDays';
import useError from '../../../utils/react/hooks/useError';
import useSuccess from '../../../utils/react/hooks/useSuccess';
import useTab from '../../../utils/react/hooks/useTab';
import useUserPermissions from '../../../utils/react/hooks/useUserPermissions';
import { TutorialDetails } from '../types';

import {
  AddButton, AlertContainer, ControlBar, LoadingWrapper,
} from './index.styles';
import TutorialAttendeesDialog from './TutorialAttendeesDialog';
import {
  getStaffTutorialState, getTutorialFormFields, TutorialUserType,
} from './utils';

const TODAY = dayjs().startOf('day');

const INITIAL_TAB_OPTIONS = [
  {
    key: 'all',
    label: 'All',
  },
];

type Option = {
  label: string,
  onClick: () => void,
}

interface StaffTutorialsPageProps {
  userGroups: string[]
}

const StaffTutorialsPage = ({ userGroups }: StaffTutorialsPageProps) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  const { data: teachers } = useGetStaffsQuery({ group: UserGroup.Teachers });

  // Get user details
  const userId = getCurrentUserId();
  const permissions = useUserPermissions();
  const { data: staff } = useGetStaffQuery({ staffId: userId || '' });

  const isTeacher = userGroups.includes(UserGroup.Teachers);
  const isAdmin = userGroups.includes(UserGroup.Admins);
  const isOnlyTeacher = isTeacher && !isAdmin;

  const { data: allCourses } = useGetCoursesQuery({});

  const [courses, setCourses] = useState<Course[] | null>();
  const [tabOptions, setTabOptions] =
    useState<{ key: string, label: string }[]>(INITIAL_TAB_OPTIONS);
  const [tab, setTab] = useTab({ default: 'all' });
  const [isYourTutorials, setIsYourTutorials] = useState(isTeacher);

  const [createFormOpen, setCreateFormOpen] = useState(false);
  const [newTutorialForm, setNewTutorialForm] = useState<Values>({});
  const [newTutorialFormFields, setNewTutorialFormFields] = useState<Fields>(
    getTutorialFormFields(isOnlyTeacher));

  const [attendeesDialogOpen, setAttendeesDialogOpen] = useState(false);
  const [selectedTutorial, setSelectedTutorial] = useState<Tutorial>();

  useEffect(() => {
    if (permissions && allCourses) {
      const coursesList = getCoursesFromPermissions(permissions, allCourses.data);
      setCourses(coursesList);
      setTabOptions(INITIAL_TAB_OPTIONS.concat(coursesList.map(
        (course) => ({ ...course, key: course.id }),
      ).sort((a, b) => b.label.localeCompare(a.label))));
    }
  }, [permissions, allCourses]);

  const [confirmModalDetails, setConfirmModalDetails] = useState<{
    title: string, options: Option[]
  }>({
    title: '', options: [],
  });
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const openCreateForm = () => {
    const defaultFormFields = getTutorialFormFields(isOnlyTeacher);
    setNewTutorialFormFields({
      ...defaultFormFields,
      course: {
        ...defaultFormFields.course,
        typeOptions: courses?.map((course) => ({
          label: course.shortLabel,
          value: course.id,
        })),
      },
      host: {
        ...defaultFormFields.host,
        ...((teachers && isAdmin) && {
          typeOptions: teachers?.data.map((x: ShortStaff) => ({
            label: `${x.name}`,
            value: x.staffId,
          })),
        }),
        ...((staff?.data && isOnlyTeacher && userId) && {
          typeOptions: [{
            label: staff?.data.name,
            value: userId,
          }],
        }),
      },
    });
    setNewTutorialForm({
      course: (courses && tab === 'all' && isTeacher) ? courses[0]?.id : tab,
      startDatetime: dayjs().valueOf(),
      host: isTeacher ? userId : undefined,
      capacity: 3,
      duration: 60,
      description: 'Ask any questions you have about the subject!',
      joinLink: 'https://us02web.zoom.us/j/4494961628',
      repeat: 0,
    });
    setCreateFormOpen(true);
  };

  const closeCreateForm = () => setCreateFormOpen(false);

  // Earliest date to fetch tutorials
  const [startDate, setStartDate] = useState<Dayjs>(addDays(TODAY, -14));

  // Latest date to fetch tutorials
  const [endDate, setEndDate] = useState<Dayjs>(addDays(TODAY, 14));

  const { data: tutorialsInRange, isLoading } = useGetTutorialsInRangeQuery({
    startDatetime: startDate.valueOf(),
    endDatetime: endDate.valueOf(),
  });
  const [deleteTutorial,
    { isError: isDeleteError,
      isSuccess: isDeleteSuccess,
      error: deleteError,
      isLoading: isDeleteLoading }] = useDeleteTutorialMutation();
  const [createTutorial,
    { isSuccess: isCreateSuccess,
      error: createError,
      isLoading: isCreateLoading }] = useNewTutorialMutation();
  const [updateTutorial,
    { isSuccess: isUpdateSuccess,
      isError: isUpdateError,
      error: updateError,
      isLoading: isUpdateLoading }] = useUpdateTutorialMutation();

  const handleDelete = (tutorial: Tutorial) => {
    if (tutorial.linkedTutorials) {
      setConfirmModalDetails({
        title: 'Delete Recurring Tutorial',
        options: [{
          label: 'Just this tutorial',
          onClick: () => deleteTutorial({
            course: tutorial.course,
            id: tutorial.startDatetimeIdentifier,
          }),
        },
        {
          label: 'This and following tutorials',
          onClick: () => deleteTutorial({
            course: tutorial.course,
            id: tutorial.startDatetimeIdentifier,
            deleteScope: TutorialScope.Following,
          }),
        },
        {
          label: 'All tutorials',
          onClick: () => deleteTutorial({
            course: tutorial.course,
            id: tutorial.startDatetimeIdentifier,
            deleteScope: TutorialScope.All,
          }),
        }],
      });
      setConfirmModalOpen(true);
    } else {
      deleteTutorial({
        course: tutorial.course,
        id: tutorial.startDatetimeIdentifier,
      });
    }
  };

  const handleUpdate = (tutorial: Tutorial, newValues?: Values) => {
    if (tutorial.linkedTutorials) {
      setConfirmModalDetails({
        title: 'Update Recurring Tutorial',
        options: [{
          label: 'Just this tutorial',
          onClick: () => updateTutorial({
            course: tutorial.course,
            id: tutorial.startDatetimeIdentifier,
            attributes: newValues,
          }),
        },
        {
          label: 'This and following tutorials',
          onClick: () => updateTutorial({
            course: tutorial.course,
            id: tutorial.startDatetimeIdentifier,
            attributes: newValues,
            updateScope: TutorialScope.Following,
          }),
        },
        {
          label: 'All tutorials',
          onClick: () => updateTutorial({
            course: tutorial.course,
            id: tutorial.startDatetimeIdentifier,
            attributes: newValues,
            updateScope: TutorialScope.All,
          }),
        }],
      });
      setConfirmModalOpen(true);
    } else {
      updateTutorial({
        course: tutorial.course,
        id: tutorial.startDatetimeIdentifier,
        attributes: newValues,
      });
    }
  };

  const handleShowAttendees = (tutorial: Tutorial) => {
    setSelectedTutorial(tutorial);
    setAttendeesDialogOpen(true);
  };

  const toCalendarEntry = (tutorial: Tutorial): CalendarEntry<TutorialDetails> => {
    let user: TutorialUserType = TutorialUserType.Teacher;

    if (tutorial.host === userId) {
      user = TutorialUserType.Host;
    }

    if (isAdmin) {
      user = TutorialUserType.Admin;
    }

    const deleteButton: Button = {
      fn: () => handleDelete(tutorial),
      label: 'Delete',
      color: 'error',
      loading: isDeleteLoading,
    };

    const attendeesButton: Button = {
      fn: () => handleShowAttendees(tutorial),
      label: 'Attendees',
      color: 'info',
    };

    const saveButton: Button = {
      fn: (values?: Values, newValues?: Values) => handleUpdate(tutorial, newValues),
      label: 'Save',
      loading: isUpdateLoading,
      color: 'success',
      variant: 'contained',
      disabled: (newValues?: Values) => Object.keys(newValues || {}).length === 0,
    };

    const joinButton: Button = {
      fn: () => window.open(tutorial.joinLink),
      label: 'Join',
      color: 'success',
    };

    const baseAttributes = getBaseAttributes(tutorial, allCourses?.data ?? []);

    return {
      ...baseAttributes,
      details: {
        ...baseAttributes.details,
        properties: {
          ..._.pick(tutorial, [
            'duration',
            'host',
            'description',
            'joinLink',
            'capacity',
          ]),
          studentCount: tutorial.attendees ? tutorial.attendees.length : 0,
        },
        state: getStaffTutorialState(
          tutorial, user, deleteButton, attendeesButton, saveButton, joinButton),
      },
    };
  };

  const checkTutorial = (tutorial: Tutorial) => {
    if (tab === 'all') {
      if (isYourTutorials) {
        return tutorial.host === userId;
      }
      return true;
    }
    if (isYourTutorials) {
      return tutorial.course === tab && tutorial.host === userId;
    }
    return tutorial.course === tab;
  };

  useError(isDeleteError, deleteError);
  useError(isUpdateError, updateError);

  useSuccess(isDeleteSuccess, 'Tutorial(s) Deleted');
  useSuccess(isCreateSuccess,
    newTutorialForm.repeat ? `${newTutorialForm.totalRepeats} Tutorials Created` : 'Tutorial Created',
    closeCreateForm);
  useSuccess(isUpdateSuccess, 'Tutorial(s) Updated');

  const [tutorials, setTutorials] = useState<CalendarEntry<TutorialDetails>[]>([]);

  useEffect(() => {
    if (tutorialsInRange && allCourses) {
      setTutorials(tutorialsInRange?.data.filter(checkTutorial)
        .map((tutorial: Tutorial) => toCalendarEntry(tutorial)));
    }
  }, [tutorialsInRange, allCourses, tab, isYourTutorials]);

  // Update form fields if repeat is selected
  useEffect(() => {
    if (newTutorialForm.repeat) {
      setNewTutorialFormFields({
        ...newTutorialFormFields,
        totalRepeats: {
          label: 'Occurrences',
          editable: true,
          type: 'number',
        },
      });
    } else {
      setNewTutorialFormFields(_.omit(newTutorialFormFields, ['totalRepeats']));
    }
  }, [newTutorialForm.repeat]);

  if (!courses || !permissions) {
    return (
      <SkeletonStack />
    );
  }

  if (courses && courses?.length === 0) {
    // If courses has loaded but there are no courses, show no permission message
    return (
      <NoPermissionMessage />
    );
  }

  return (
    <>
      {(courses.length > 1 && tabOptions) ? (
        <Tabs
          options={tabOptions}
          selectedKey={tab}
          onChange={(key) => setTab(key)}
        />
      ) : <Skeleton />}
      <AlertContainer>
        <Alert severity="info">
          All teachers share the same Zoom credentials to run tutorials:{' '}
          <b>admin@cognitotuition.com (PW: Wiad8870)</b>. Please join the call via the
          meeting ID <b>449 496 1628</b>, then move yourself into a breakout room to
          run the tutorial.
          <br />
          <br />
          <b>
            Note: Students are restricted from booking empty tutorials
            within 24 hours of its start time.
          </b>
        </Alert>
      </AlertContainer>
      <ContentCard variant="outlined" title="Tutorials Calendar">
        <ControlBar>
          {isTeacher && (
            <FormControlLabel
              control={(
                <Switch
                  checked={isYourTutorials}
                  onChange={() => setIsYourTutorials(!isYourTutorials)}
                />
              )}
              label={isMd ? 'Your Tutorials Only' : 'Yours Only'}
            />
          )}
          <AddButton
            startIcon={<AddRounded />}
            onClick={openCreateForm}
          >
            Add
          </AddButton>
        </ControlBar>
        {
          isLoading ? (
            <LoadingWrapper>
              <CircularProgress />
            </LoadingWrapper>
          ) : (
            <WeekCalendar
              data={tutorials}
              renderContent={(entry) => (
                <EventCell
                  key={entry.id}
                  startDatetime={entry.startDatetime}
                  duration={entry.details.duration}
                  title={entry.details.title}
                  color={entry.details.color}
                  state={entry.details.state}
                  propertyFields={{
                    ..._.pick(tutorialsInRange?.metadata, [
                      'description',
                      'host',
                      'duration',
                      'joinLink',
                      'capacity',
                    ]),
                    studentCount: {
                      label: 'Attendees',
                      editable: false,
                      type: 'number',
                    },
                    ...entry.details.extraFields,
                  }}
                  propertyValues={entry.details.properties}
                  fetchProperties={entry.details.fetchProperties}
                  showStatus
                />
              )}
              minDate={startDate}
              setMinDate={setStartDate}
              maxDate={endDate}
              setMaxDate={setEndDate}
            />
          )
        }
      </ContentCard>
      {selectedTutorial && (
        <TutorialAttendeesDialog
          handleClose={() => setAttendeesDialogOpen(false)}
          open={attendeesDialogOpen}
          selectedTutorial={selectedTutorial}
          isAdmin={isAdmin}
        />
      )}
      <Modal
        open={confirmModalOpen}
        handleClose={() => setConfirmModalOpen(false)}
        title={confirmModalDetails.title}
      >
        <List sx={{ pt: 0 }}>
          {
            confirmModalDetails.options.map((option) => (
              <ListItemButton
                key={option.label}
                onClick={() => {
                  option.onClick();
                  setConfirmModalOpen(false);
                }}
              >
                <ListItemText primary={option.label} />
              </ListItemButton>
            ))
          }
        </List>
      </Modal>
      <DetailsModal
        open={createFormOpen}
        handleClose={closeCreateForm}
        title="Add New Tutorial"
        fields={newTutorialFormFields}
        values={newTutorialForm}
        error={createError}
        buttons={[{
          fn: async (values?: Values) => {
            await createTutorial({
              attributes: {
                course: values?.course,
                startDatetime: values?.startDatetime,
                duration: values?.duration,
                capacity: values?.capacity,
                host: values?.host,
                description: values?.description,
                joinLink: values?.joinLink,
                ...(values?.repeat && {
                  repeat: true,
                  totalRepeats: values?.totalRepeats,
                  frequency: values?.repeat,
                }),
              },
            });
          },
          label: 'Add',
          color: 'darkPrimary',
          loading: isCreateLoading,
        }]}
        onFieldChange={(key, value) => {
          setNewTutorialForm({
            ...newTutorialForm,
            [key]: value,
          });
        }}
      />
      {isDeleteLoading && (
        <Backdrop open>
          <CircularProgress />
        </Backdrop>
      )}
    </>
  );
};
export default StaffTutorialsPage;
